import { RocheUIRichText } from '@repo/roche-ui';
import { StoryblokRichText } from '@storyblok/react';
import { StoryblokRichTextDocumentNode } from '@storyblok/richtext';
import React, { FC } from 'react';
import { BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { Link } from '../../services/navigation/types';

export interface TextLinkComponentProps {
  text?: string;
  target?: string;
  left_icon?: string;
  right_icon?: string;
  link: Link;
}

const unwantedMarks = ['textStyle', 'superscript', 'subscript'];
/**
 * Remove unwanted marks from the Storyblok rich text document node
 * such as 'textStyle', 'superscript', and 'subscript'.
 */
const removeUnwantedMarks = (node: StoryblokRichTextDocumentNode) => {
  const newNode: StoryblokRichTextDocumentNode = { ...node };

  if (Array.isArray(newNode.marks)) {
    newNode.marks = newNode.marks.filter((mark: { type }) => !unwantedMarks.includes(mark.type));
  }
  if (Array.isArray(newNode.content)) {
    newNode.content = newNode.content.map((child) => removeUnwantedMarks(child));
  }
  return newNode;
};

const isEmptyParagraph = (node: StoryblokRichTextDocumentNode): boolean => {
  if (node?.type !== 'paragraph') {
    return false;
  }
  // A paragraph is empty if its content is missing or just an empty array.
  return !Array.isArray(node.content) || node.content.length === 0;
};

const isStandaloneLink = (node: StoryblokRichTextDocumentNode) =>
  node?.type === 'blok' &&
  (node.attrs?.body?.[0]?.component === 'roche-text-link' ||
    node.attrs?.body?.[0]?.component === 'roche-cookie-banner-link');

const isHardBreakParagraph = (node: StoryblokRichTextDocumentNode) =>
  node?.type === 'paragraph' &&
  Array.isArray(node.content) &&
  node.content?.length > 0 &&
  node.content.every((c) => c.type === 'hard_break');

const endsWithLink = (node: StoryblokRichTextDocumentNode) => {
  if (node?.type !== 'paragraph' || !Array.isArray(node.content) || node.content.length === 0) {
    return false;
  }
  const lastContentItem = node.content[node.content.length - 1];
  return isStandaloneLink(lastContentItem);
};

/**
 * This function merges a link blok with an adjacent paragraph block
 * to ensure the link renders inside the paragraph, not as a separate block.
 * This mimics the behavior of the old string-replacement `forceLinksInsideParagraphs`.
 *
 * It looks for a `paragraph` node that is immediately followed by a `roche-text-link`
 * or `roche-cookie-banner-link` blok. It then merges the link into the paragraph's
 * content and also merges the next paragraph, if one exists.
 */
const applyMergingLogic = (nodes: StoryblokRichTextDocumentNode[]): StoryblokRichTextDocumentNode[] => {
  return nodes.reduce((acc: StoryblokRichTextDocumentNode[], currentNode) => {
    const lastNode = acc[acc.length - 1];

    if (lastNode?.type === 'paragraph') {
      // Case 1: Current node is a LINK. Merge it with a space.
      if (isStandaloneLink(currentNode)) {
        const uniqueLinkNode = { ...currentNode, attrs: { ...currentNode.attrs, id: currentNode.attrs.body[0]._uid } };
        const newContent = [...(lastNode.content || []), { type: 'text', text: ' ' }, uniqueLinkNode];
        acc[acc.length - 1] = { ...lastNode, content: newContent };
        return acc;
      }

      // Case 2: Current node is a PARAGRAPH that follows a LINK. Merge it with a space.
      if (endsWithLink(lastNode) && currentNode.type === 'paragraph') {
        const newContent = [...(lastNode.content || []), { type: 'text', text: ' ' }, ...(currentNode.content || [])];
        acc[acc.length - 1] = { ...lastNode, content: newContent };
        return acc;
      }

      // Case 3: Current node is an empty line-break paragraph. DISCARD IT.
      // This connects paragraphs to links even if there's an empty line between them.
      if (isHardBreakParagraph(currentNode)) {
        return acc;
      }
    }

    // Default case: The node cannot be merged, so just add it.
    acc.push(currentNode);
    return acc;
  }, []);
};

const traverseAndProcessNodes = (
  node: StoryblokRichTextDocumentNode,
): StoryblokRichTextDocumentNode | StoryblokRichTextDocumentNode[] => {
  let processedNode = node;

  // 1. If the node has content, recursively process its children first.
  if (Array.isArray(processedNode.content)) {
    // Use flatMap to handle children that might get unnested by the recursive call.
    const processedChildren = processedNode.content
      .flatMap((childNode) => traverseAndProcessNodes(childNode))
      .filter(Boolean);

    // 2. Apply the paragraph/link merging logic to the processed children.
    const mergedChildren = applyMergingLogic(processedChildren);

    processedNode = {
      ...processedNode,
      content: mergedChildren,
    };
  }

  // 3. After processing children, apply flattening to the current node.
  return flattenBlokContent(processedNode);
};

/**
 * Flatten the document structure:
 * 1. If an item is a "blok" with multiple items in its `attrs.body` array,
 *    we take those items out to the top level of the object
 * 2. If an item is a standard node (like paragraph, list, etc.),
 *    we leave it as is.
 *
 * This is done to ensure that the document structure is flat
 * as the StoryblokRichText component seems to expect a flat structure.
 */
const flattenBlokContent = (blok: StoryblokRichTextDocumentNode) => {
  if (blok.type === 'blok' && Array.isArray(blok.attrs?.body)) {
    return blok.attrs.body.map((nestedBlok) => ({
      type: 'blok',
      attrs: {
        // eslint-disable-next-line no-underscore-dangle
        id: nestedBlok._uid, // Use the component's unique ID.
        body: [nestedBlok], // Place the component inside the body array.
      },
    })) as StoryblokRichTextDocumentNode[];
  }
  return blok;
};

const getOppositeDirValue = (dirValue: string) => (dirValue === 'ltr' ? 'rtl' : 'ltr');

const getGlobalDirValue = () => {
  const dirValue = typeof window !== 'undefined' ? document.documentElement.getAttribute('dir') : 'ltr';
  return dirValue || 'ltr';
};

type RocheRichtextProps = BlokToComponentProps<{
  text: StoryblokRichTextDocumentNode;
  footnote: boolean;
  reverse_text_direction: boolean;
  type: string;
}>;

export const RocheRichtext: FC<RocheRichtextProps> = ({ blok, slot }) => {
  const isValidRichtext = blok && blok.text && typeof blok.text === 'object' && blok.text.type === 'doc';
  if (!isValidRichtext) {
    // Return an empty RocheUIRichText, in case it was used as a "spacer"
    return (
      <RocheUIRichText slot={slot} isFootnote={blok.footnote} isFooterType={blok.type === 'footer'}></RocheUIRichText>
    );
  }

  // 1. Sanitize the document.
  const sanitizedDoc = removeUnwantedMarks(blok.text);

  // 2. Recursively process and flatten all nodes.
  const processedNodes = sanitizedDoc.content?.flatMap((node) => traverseAndProcessNodes(node)).filter(Boolean) || [];

  // 3. Catch the Paragraph -> Link -> Paragraph sequence at the root.
  const finalContent = applyMergingLogic(processedNodes);

  // 4. Find the first and last valid content nodes and slice the array.
  const firstContentIndex = finalContent.findIndex((node) => !isEmptyParagraph(node));

  // If the array is completely empty or contains only empty paragraphs, we don't render anything.
  if (firstContentIndex === -1) {
    return null;
  }

  const lastContentIndex = finalContent.findLastIndex((node) => !isEmptyParagraph(node));
  const trimmedContent = finalContent.slice(firstContentIndex, lastContentIndex + 1);

  // 5. Create the final document for the renderer with the trimmed content.
  const finalDoc = {
    ...sanitizedDoc,
    content: trimmedContent,
  } as any; // don't know how to type this

  const textDirection = blok?.reverse_text_direction ? getOppositeDirValue(getGlobalDirValue()) : getGlobalDirValue();

  return (
    <RocheUIRichText slot={slot} dir={textDirection} isFootnote={blok.footnote} isFooterType={blok.type === 'footer'}>
      <StoryblokRichText doc={finalDoc} />
    </RocheUIRichText>
  );
};
